import { format } from 'date-fns';

import { CreateReportSchedule } from '@futrli-sdk/report_scheduling';
import { WorkspaceMember } from '@futrli-sdk/people';

export type ReportScheduleRecurrence =
  | 'do-not-repeat'
  | 'daily'
  | 'weekly'
  | 'monthly'
  | 'quarterly'
  | 'annually';

export type ReportScheduleEndType = 'end-date' | 'end-after-occurrences' | 'does-not-end';

export const THE_LAST = 'the_last';

const DATE_FORMAT = 'yyyy-MM-dd';
const DEFAULT_REPEATING_FREQUENCY = 1;
const LAST_DAY_OF_MONTH = 31;

type ScheduleEndFields = Pick<CreateReportSchedule, 'end_type' | 'end_date' | 'end_after_occurrences'>;

/** Builds the API end fields from the selected recurrence end settings. */
const getEndFields = (
  endType: ReportScheduleEndType,
  occurrencesValue: string,
  endDate: Date
): ScheduleEndFields => ({
  end_type: endType,
  end_date: endType === 'end-date' ? format(endDate, DATE_FORMAT) : null,
  end_after_occurrences: endType === 'end-after-occurrences' ? Number(occurrencesValue) : null
});

/** Maps quarterly and annual recurrences to the API's monthly period. */
const getPeriod = (recurrence: ReportScheduleRecurrence): CreateReportSchedule['period'] =>
  recurrence === 'quarterly' || recurrence === 'annually' ? 'monthly' : recurrence;

export interface BuildSchedulePayloadParams {
  reportId: string;
  reportName: string;
  recurrence: ReportScheduleRecurrence;
  recurrenceStartDate: Date;
  recurrenceRepeatValue: string;
  recurrenceEndType: ReportScheduleEndType;
  occurancesValue: string;
  recurrenceEndDate: Date;
  recurrenceWeekDay: string;
  recurrenceMonthDay: string;
  selectedUsers: WorkspaceMember[];
  emailSubject: string;
  emailBody: string;
  reviewBeforeSending: boolean;
}

/**
 * Maps the schedule form's recurrence model into the report scheduling API
 * contract. This is domain translation and is intentionally kept outside the UI.
 */
export const buildSchedulePayload = ({
  reportId,
  reportName,
  recurrence,
  recurrenceStartDate,
  recurrenceRepeatValue,
  recurrenceEndType,
  occurancesValue,
  recurrenceEndDate,
  recurrenceWeekDay,
  recurrenceMonthDay,
  selectedUsers,
  emailSubject,
  emailBody,
  reviewBeforeSending
}: BuildSchedulePayloadParams): Partial<CreateReportSchedule> => {
  const users = selectedUsers.map(user => ({ id: user.id, email_address: user.email_address }));

  const sharedPayload = {
    name: reportName,
    report: reportId,
    period: getPeriod(recurrence),
    start_date: recurrence === 'do-not-repeat'
      ? format(new Date(), DATE_FORMAT)
      : format(recurrenceStartDate, DATE_FORMAT),
    email_subject: emailSubject,
    email_body: emailBody,
    users,
    approval_email_user: recurrence === 'do-not-repeat' ? false : reviewBeforeSending
  };

  if (recurrence === 'do-not-repeat') {
    return {
      ...sharedPayload,
      end_type: 'does-not-end',
      end_date: null,
      end_after_occurrences: null,
      repeating_frequency: DEFAULT_REPEATING_FREQUENCY,
      day_of_month: null,
      weekday: null
    };
  }

  const recurringPayload = {
    ...sharedPayload,
    ...getEndFields(recurrenceEndType, occurancesValue, recurrenceEndDate),
    repeating_frequency: Number(recurrenceRepeatValue),
    day_of_month: null,
    weekday: null
  };

  if (recurrence === 'daily') {
    return recurringPayload;
  }

  if (recurrence === 'weekly') {
    return {
      ...recurringPayload,
      weekday: recurrenceWeekDay as CreateReportSchedule['weekday']
    };
  }

  return {
    ...recurringPayload,
    day_of_month: recurrenceMonthDay === THE_LAST ? LAST_DAY_OF_MONTH : Number(recurrenceMonthDay)
  };
};
